import { GetSiteDataQuery, GetBlogDataQuery, GetHomeDataQuery, GetAboutDataQuery, GetPortfolioDataQuery, GetTestimonialDataQuery, GetContactDataQuery, GetPageDataQuery } from '../generated/graphql';
import type { ApolloQueryResult } from '@apollo/client/core';

// Minimal mock data used during prerender to avoid live API calls and rate limits.
//
// Policy & rationale:
// - During server-side prerender we avoid hitting the live GraphCMS endpoint to
//   prevent rate-limiting and to produce deterministic builds.
// - These mocked objects mirror the minimal shape required by the app's
//   components. Use the `get*QueryResultMock()` helpers to produce a typed
//   QueryResult-like object when a consumer expects a full Apollo response, or
//   use `get*Mock` directly if you need just the raw data.
// - Prefer adding new mock helpers here when introducing new queries, and
//   keep mocks intentionally minimal to avoid masking real-data issues.
//
// Usage examples:
// - In `DataService`, `getSiteData()` returns an Apollo-like result for runtime
//   parity, and `getSiteDataData()` returns the unwrapped `GetSiteDataQuery` for
//   simple consumption in components.
// - When adding new queries add a mock `getXMock` and optionally a
//   `getXQueryResultMock()` helper so server-side code can return the right
//   shape without `as any` casts.

export const getSiteDataMock: GetSiteDataQuery = {
  __typename: 'Query',
  sites: [
    {
      __typename: 'Site',
      title: 'Prerender Site',
      shortTitle: 'Prerender',
      keywords: null,
      description: null,
      robots: null,
      copyright: null,
      buildTools: [],
      resources: [],
      siteVersions: [],
      logo: null,
      backgroundImages: [
        {
          __typename: 'Asset',
          url: '/assets/placeholder-desktop.jpg',
          mobile_url: '/assets/placeholder-mobile.jpg',
          tablet_url: '/assets/placeholder-tablet.jpg'
        }
      ]
    }
  ]
};

export const getBlogDataMock: GetBlogDataQuery = {
  __typename: 'Query',
  blogPosts: [
    {
      __typename: 'BlogPost',
      title: 'Prerender Post',
      slug: 'prerender-post',
      date: new Date().toISOString(),
      content: 'This is prerender placeholder content.',
      tags: [],
      images: []
    }
  ]
};

// Return properly typed ApolloQueryResult objects so server-side mocks satisfy
// the same shape Apollo returns and TypeScript stays strict.
export function getSiteDataQueryResultMock(): ApolloQueryResult<GetSiteDataQuery> {
  return { data: getSiteDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetSiteDataQuery>;
}

export function getBlogDataQueryResultMock(): ApolloQueryResult<GetBlogDataQuery> {
  return { data: getBlogDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetBlogDataQuery>;
}

// Minimal mocks for other queries to avoid live GraphCMS calls during prerender.
export const getHomeDataMock: GetHomeDataQuery = {
  __typename: 'Query',
  homeItems: [
    {
      __typename: 'HomeItem',
      name: 'Prerender Home Item',
      title: 'Prerender Home Item',
      text: 'Prerender excerpt',
      order: 0
    }
  ]
};

export function getHomeDataQueryResultMock(): ApolloQueryResult<GetHomeDataQuery> {
  return { data: getHomeDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetHomeDataQuery>;
}

export const getAboutDataMock: GetAboutDataQuery = {
  __typename: 'Query',
  aboutContents: [
    {
      __typename: 'AboutContent',
      name: 'Prerender About',
      statsTitle: null,
      stats: [],
      text: 'This is prerender about content.',
      images: []
    }
  ]
};

export function getAboutDataQueryResultMock(): ApolloQueryResult<GetAboutDataQuery> {
  return { data: getAboutDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetAboutDataQuery>;
}

export const getPortfolioDataMock: GetPortfolioDataQuery = {
  __typename: 'Query',
  portfolioItems: []
};

export function getPortfolioDataQueryResultMock(): ApolloQueryResult<GetPortfolioDataQuery> {
  return { data: getPortfolioDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetPortfolioDataQuery>;
}

export const getTestimonialDataMock: GetTestimonialDataQuery = {
  __typename: 'Query',
  testimonialItems: []
};

export function getTestimonialDataQueryResultMock(): ApolloQueryResult<GetTestimonialDataQuery> {
  return { data: getTestimonialDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetTestimonialDataQuery>;
}

export const getContactDataMock: GetContactDataQuery = {
  __typename: 'Query',
  contactContents: [
    {
      __typename: 'ContactContent',
      name: 'Prerender Contact',
      socialTitle: null,
      socialContent: null,
      statusTitle: null,
      statusContent: null,
      transactTitle: null,
      transactContent: null,
      formTitle: 'Prerender Contact Form'
    }
  ]
};

export function getContactDataQueryResultMock(): ApolloQueryResult<GetContactDataQuery> {
  return { data: getContactDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetContactDataQuery>;
}

export const getPageDataMock: GetPageDataQuery = {
  __typename: 'Query',
  pagesSetup: [
    { __typename: 'PageSetup', name: 'home', title: 'Home', text: null, sidebarTitle: null, url: '/', order: 1 },
    { __typename: 'PageSetup', name: 'about', title: 'About', text: null, sidebarTitle: null, url: '/about', order: 2 },
    { __typename: 'PageSetup', name: 'contact', title: 'Contact', text: null, sidebarTitle: null, url: '/contact', order: 3 }
  ]
};

export function getPageDataQueryResultMock(): ApolloQueryResult<GetPageDataQuery> {
  return { data: getPageDataMock, loading: false, networkStatus: 7, partial: false, dataState: 'complete' } as ApolloQueryResult<GetPageDataQuery>;
}
